import React, { useContext, useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";

import LayoutMasterDetail from "layouts/LayoutMasterDetail";
import LiveStudentItem from "features/track-vehicle/LiveStudentItem";
import LivePersonelItem from "features/track-vehicle/LivePersonelItem";
import LoadingSpinner from "shared/components/UIElements/LoadingSpinner";

import { StoreContext } from "shared/context/store-context";
import { getTodayInTimezone } from "shared/utility/date";
import { TRAVEL_STATUS_TYPE, TRAVEL_STATUS_TYPE_EXPLANATIONS } from "shared/util/types";
import { COMPANY_DOMAINS, COMPANY_FEATURES } from "@routebot/constants";
import { useHttpClient } from "shared/hooks/http-hook";
import { AuthContext } from "shared/context/auth-context";
import { useBundledRouteApi } from "features/bundled-routes/hooks/useBundledRouteApi";

const BaseTrackVehicleList = ({ mode = COMPANY_DOMAINS.STUDENT }) => {
  const { t } = useTranslation();
  const auth = useContext(AuthContext);
  const { sendRequest } = useHttpClient();

  const { vehicleRoutes, schoolOptions, standartPersonelServices, shiftPersonelServices, storeReady } =
    useContext(StoreContext);

  const { listBundledRoutes } = useBundledRouteApi();
  const [bundledRoutes, setBundledRoutes] = useState([]);
  const bundledEnabled =
    mode !== COMPANY_DOMAINS.PERSONEL && !!auth?.company?.features?.[COMPANY_FEATURES.BUNDLED_STUDENT_ROUTES];

  const [travelStatusMap, setTravelStatusMap] = useState({});

  // Bundled routes live outside the global store (feature-local). Fetch them for the student track
  // list ONLY when the company has the feature; otherwise the list behaves exactly as before.
  useEffect(() => {
    if (!bundledEnabled) return;
    let alive = true;
    (async () => {
      const data = await listBundledRoutes();
      if (alive) setBundledRoutes(Array.isArray(data) ? data : []);
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bundledEnabled]);

  const itemType = mode === COMPANY_DOMAINS.PERSONEL ? "personelservice" : "vehicleRoute";
  const listName = "main_live";

  // "Bugün" company timezone'una göre hesaplanır; Travel.day de aynı güne göre yazılıyor.
  const companyTimezone = auth.company?.timezone || auth.timezone;
  const date = getTodayInTimezone(companyTimezone, "yyyy-M-d");
  const todayForTravel = getTodayInTimezone(companyTimezone);

  const fetchTodayStatuses = useCallback(async () => {
    try {
      const responseData = await sendRequest(
        `${process.env.REACT_APP_BACKEND_URL}/track-vehicle/today-statuses`,
        "POST",
        JSON.stringify({ day: todayForTravel }),
        { "Content-Type": "application/json", Authorization: "Bearer " + auth.token },
      );
      const map = {};
      for (const travel of responseData.travels) {
        const key = travel.route || travel.personelservice || travel.bundledroute;
        if (key) {
          map[key] = travel.status;
        }
      }
      setTravelStatusMap(map);
    } catch (err) {}
  }, [sendRequest, auth.token, todayForTravel]);

  useEffect(() => {
    if (storeReady) {
      fetchTodayStatuses();
    }
  }, [storeReady, fetchTodayStatuses]);

  let pageItems = [];
  if (mode === COMPANY_DOMAINS.PERSONEL) {
    pageItems = [...standartPersonelServices, ...shiftPersonelServices];
  } else {
    // Student track list = single-school vehicle routes + independent bundled (multi-school) routes.
    pageItems = [...vehicleRoutes, ...bundledRoutes.map((r) => ({ ...r, _isBundled: true }))];
  }

  const listColumns = [];

  if (schoolOptions?.length > 1) {
    listColumns.push({
      field: "school",
      headerName: t("client"),
      width: 350,
      valueGetter: (params) => {
        if (params.row?._isBundled) return t("bundledRoutes.clientLabel");
        return schoolOptions.find((school) => school.value === params.row?.school?.id)?.label || "-";
      },
    });
  }

  listColumns.push({ field: "name", headerName: t("name"), width: 350 });
  listColumns.push({
    field: "type",
    headerName: t("routeType"),
    width: 200,
    valueGetter: (params) => {
      // Bundled routes carry routeType ("PICK_UP"/"DROP_OFF") instead of the numeric legacy `type`.
      if (params.row?._isBundled) {
        return params.row?.routeType === "DROP_OFF" ? t("dropOff") : t("pickUp");
      }
      return params.row?.type?.toString() === "200" ? t("dropOff") : t("pickUp");
    },
  });

  if (mode === COMPANY_DOMAINS.PERSONEL) {
    listColumns.push({
      field: "driver",
      headerName: t("driver"),
      width: 300,
      valueGetter: (params) =>
        Array.isArray(params.row?.drivers) && params.row.drivers.length > 0
          ? params.row.drivers.map((driver) => driver.name).join(", ")
          : "-",
    });
    listColumns.push({
      field: "shift",
      headerName: t("shiftpersonelservice.title"),
      width: 300,
      valueGetter: (params) => (params.row?.isshift ? t("yes") : t("no")),
    });
  } else {
    listColumns.push({
      field: "driver",
      headerName: t("driver"),
      width: 300,
      valueGetter: (params) =>
        Array.isArray(params.row?.driver) && params.row.driver.length > 0
          ? params.row.driver.map((driver) => driver.name).join(", ")
          : "-",
    });
  }

  listColumns.push({ field: "plate", headerName: t("plate"), width: 200 });
  listColumns.push({
    field: "status",
    headerName: date,
    width: 200,
    cellClassName: (params) => {
      const travelStatus = travelStatusMap[params.row.id];
      const status = travelStatus !== undefined ? travelStatus : TRAVEL_STATUS_TYPE.NOTSTARTED;
      return status === TRAVEL_STATUS_TYPE.NOTSTARTED ? "processongoing" : "processdone";
    },
    renderCell: (params) => {
      const travelStatus = travelStatusMap[params.row.id];
      const status = travelStatus !== undefined ? travelStatus : TRAVEL_STATUS_TYPE.NOTSTARTED;
      return (
        <div className={"travelStatusLiveIndicator"}>
          {t(TRAVEL_STATUS_TYPE_EXPLANATIONS.find((exp) => exp.value === status)?.label || "-")}
        </div>
      );
    },
  });

  if (!storeReady) {
    return <LoadingSpinner />;
  }

  const SelectedItemComponent = mode === COMPANY_DOMAINS.PERSONEL ? LivePersonelItem : LiveStudentItem;

  return (
    <LayoutMasterDetail
      itemType={itemType}
      items={pageItems}
      listName={listName}
      listColumns={listColumns}
      SelectedItemComponent={SelectedItemComponent}
      isAlwaysFullScreen={true}
      hideCreateNewItem={true}
      forceShowCloseButton={false}
      // standardHelpItems={standardHelpItems}
    />
  );
};

export default BaseTrackVehicleList;
